// Sinix Studio – content data (refresh, DD.NYC tone)

export const NAV_SERVICES = [
  { slug: "brand-identity", label: "Brand Identity" },
  { slug: "ui-ux", label: "UI/UX Design" },
  { slug: "digital-content", label: "Digital Content" },
  { slug: "video-editing", label: "Video Editing" },
  { slug: "photo-editing", label: "Photo Editing" },
  { slug: "photography-videography", label: "Photography & Videography" },
  { slug: "ai-workflows", label: "AI-Powered Workflows" },
  { slug: "graphic-design", label: "Graphic Design" },
];

export const NAV_INDUSTRIES = [
  { slug: "startups", label: "Startups" },
  { slug: "small-business", label: "Small Business" },
  { slug: "d2c", label: "D2C & Ecommerce" },
  { slug: "tech-saas", label: "Tech & SaaS" },
  { slug: "local-brands", label: "Local Brands" },
];

export const SERVICES = [
  {
    slug: "brand-identity",
    title: "Brand Identity",
    short: "Your brand is more than a logo—it's how people remember and trust your business. We create cohesive brand systems that communicate your values and differentiate you from competitors.",
    sublist: [
        "Logo Design", "Brand Strategy", "Brand Guidelines", "Visual Identity Systems",
        "Typography Selection", "Color Palette Development", "Business Cards & Stationery",
        "Packaging Design", "Social Media Branding", "Corporate Identity Kits",
        "Brand Refresh & Rebranding", "Marketing Collateral Design"
    ],
    starting: "₹15,000",
    overview: {
      what: "End-to-end brand identity design — from strategy to a complete visual system that scales across every touchpoint.",
      who: "Founders launching new brands, businesses rebranding, and teams that want a cohesive visual language.",
      problem: "A scattered, inconsistent brand erodes trust. We build a tight, considered identity that signals quality from the first glance.",
    },
    deliverables: [
      "Logo Design (primary + variations)",
      "Color Palette System",
      "Typography Selection",
      "Brand Guidelines Document",
      "Business Card Design",
      "Social Media Kit",
    ],
    process: [
      { t: "Discover", d: "Brand audit, audience research, mood-boarding." },
      { t: "Concept", d: "Mark exploration & directional concepts." },
      { t: "Refine", d: "Revisions, color & typography systemization." },
      { t: "Deliver", d: "Final files + guidelines + handoff support." },
    ],
    pricing: [
      { tier: "Starter", price: "₹15,000", inc: ["Primary logo + 1 variation", "Color palette", "Typography pair", "Basic guidelines PDF"], delivery: "5–7 days" },
      { tier: "Standard", price: "₹35,000", inc: ["Full mark system", "Complete color system", "Typography hierarchy", "10-page guidelines", "Business card", "Social kit"], delivery: "10–14 days", featured: true },
      { tier: "Premium", price: "₹65,000", inc: ["Logo system + sub-marks", "Extended color & motion", "Typography library", "30-page brand book", "Stationery suite", "Launch templates"], delivery: "20–25 days" },
      { tier: "Custom", price: "Custom Quote", inc: ["Tailored to your specific requirements", "Flexible deliverables", "Dedicated strategy sessions", "Priority support"], delivery: "Timeline varies" }
    ],
    faq: [
      { q: "How many logo concepts do I get?", a: "You'll see 3 distinct directions in the first round. We then refine the chosen one until it's perfect." },
      { q: "Do I own the rights to the final design?", a: "Absolutely. Full IP ownership transfers to you on final payment, including source files." },
      { q: "What files will I receive?", a: "AI, EPS, SVG, PNG (multiple sizes), PDF guidelines and a Figma library." },
      { q: "Can I get revisions after delivery?", a: "Yes — every package includes 2–3 revision rounds. Additional rounds are billed hourly." },
      { q: "Do you offer rebrands?", a: "Yes, rebranding is one of our most-requested services. We treat existing equity carefully." },
    ],
  },
  {
    slug: "ui-ux",
    title: "UI/UX Design",
    short: "We design intuitive digital experiences that are visually appealing and easy to use, helping businesses improve customer engagement and conversions.",
    sublist: [
        "User Experience Research", "User Interface Design", "Website Design", "Mobile App Design",
        "Dashboard & SaaS Design", "Wireframing", "Interactive Prototyping", "Design Systems",
        "Landing Page Design", "E-commerce Design", "User Journey Mapping", "Accessibility Optimization"
    ],
    starting: "₹25,000",
    overview: {
      what: "Product design from research to a developer-ready UI — websites, web apps and mobile screens.",
      who: "Founders building MVPs, SaaS teams improving conversion, and agencies that need production-grade design.",
      problem: "Confusing interfaces kill activation. We design flows that feel obvious and screens that feel premium.",
    },
    deliverables: ["User Research", "Wireframes", "Clickable Prototype", "Final UI Screens", "Figma Source File", "Developer Handoff"],
    process: [
      { t: "Research", d: "User interviews, competitive teardown, info architecture." },
      { t: "Wireframe", d: "Low-fidelity flows + clickable prototype." },
      { t: "Design", d: "Pixel-perfect UI with components & variants." },
      { t: "Handoff", d: "Spec, tokens & developer walkthrough." },
    ],
    pricing: [
      { tier: "Starter", price: "₹25,000", inc: ["Up to 5 screens", "1 user flow", "Mobile + desktop", "Figma file"], delivery: "7–10 days" },
      { tier: "Standard", price: "₹60,000", inc: ["Up to 15 screens", "3 flows + prototype", "Component library", "Design tokens", "Handoff doc"], delivery: "2–3 weeks", featured: true },
      { tier: "Premium", price: "₹1,20,000", inc: ["30+ screens", "Full design system", "User research report", "Interaction specs", "Dev pairing sessions"], delivery: "4–6 weeks" },
      { tier: "Custom", price: "Custom Quote", inc: ["Tailored to your specific requirements", "Flexible deliverables", "Dedicated strategy sessions", "Priority support"], delivery: "Timeline varies" }
    ],
    faq: [
      { q: "Do you write the code too?", a: "I focus on design. I work closely with your developers and can recommend trusted partners if needed." },
      { q: "Will my design be responsive?", a: "Yes — every screen is designed for mobile, tablet and desktop with proper breakpoints." },
      { q: "Can you redesign an existing product?", a: "Yes. I run a UX audit first to understand what's working before changing anything." },
      { q: "What design tools do you use?", a: "Figma is the primary tool. I deliver clean, well-organized files with components and auto-layout." },
      { q: "How do you measure success?", a: "Through usability testing, conversion metrics and qualitative user feedback after launch." },
    ],
  },
  {
    slug: "digital-content",
    title: "Digital Content",
    short: "Create compelling content that strengthens your online presence and connects with your audience across digital platforms.",
    sublist: [
        "Social Media Creatives", "Marketing Campaign Assets", "Digital Advertisements", "Banner Design",
        "Infographics", "Presentation Design", "Email Marketing Graphics", "Motion Graphics",
        "Product Catalog Design", "Event & Promotional Materials", "Content Strategy", "Creative Direction"
    ],
    starting: "₹12,000/mo",
    overview: {
      what: "Social media design, ad creatives and content systems that keep your brand consistently in front of the right people.",
      who: "DTC brands, creators, and small businesses that need a steady stream of on-brand content.",
      problem: "Inconsistent posting and off-brand creative kills momentum. We build a content engine you can run forever.",
    },
    deliverables: ["Social Media Creatives", "Ad Banners", "Reels Thumbnails", "Monthly Content Calendar", "Brand Templates", "Story Designs"],
    process: [
      { t: "Audit", d: "Review current content & competitor analysis." },
      { t: "System", d: "Templates, grids and color rules." },
      { t: "Create", d: "Monthly batches of posts, ads & stories." },
      { t: "Iterate", d: "Test what performs, double down monthly." },
    ],
    pricing: [
      { tier: "Starter", price: "₹12,000/mo", inc: ["12 social posts", "4 stories", "1 platform", "Editable templates"], delivery: "Monthly" },
      { tier: "Standard", price: "₹25,000/mo", inc: ["24 posts", "12 stories", "4 reels covers", "2 ad banners", "Content calendar"], delivery: "Monthly", featured: true },
      { tier: "Premium", price: "₹45,000/mo", inc: ["40+ posts", "Unlimited stories", "Full ad suite", "Reels editing", "Strategy call"], delivery: "Monthly" },
      { tier: "Custom", price: "Custom Quote", inc: ["Tailored to your specific requirements", "Flexible deliverables", "Dedicated strategy sessions", "Priority support"], delivery: "Timeline varies" }
    ],
    faq: [
      { q: "Do you write the captions too?", a: "I provide creative direction; copywriting can be added on. I work with trusted copywriters." },
      { q: "Can you match my existing brand?", a: "Yes — I always start with a brand audit so the new content slots in seamlessly." },
      { q: "How fast is the turnaround?", a: "Standard turnaround is 5–7 days for a monthly batch. Rush options available." },
      { q: "Which platforms do you cover?", a: "Instagram, LinkedIn, X, Facebook and YouTube thumbnails. Pinterest and TikTok on request." },
      { q: "Are revisions included?", a: "Yes, 2 revision rounds per batch are included in every package." },
    ],
  },
  {
    slug: "video-editing",
    title: "Video Editing",
    short: "Transform raw footage into engaging visual stories that capture attention and communicate your message effectively.",
    sublist: [
        "Promotional Videos", "Corporate Videos", "Social Media Reels", "YouTube Editing",
        "Product Videos", "Brand Story Videos", "Event Highlights", "Motion Graphics Integration",
        "Color Grading", "Sound Design", "Subtitle & Caption Creation", "Advertisement Editing"
    ],
    starting: "₹3,500/video",
    overview: {
      what: "From raw footage to polished, platform-ready video — reels, ads, YouTube and product videos.",
      who: "Creators, e-commerce brands, founders building personal brand on social.",
      problem: "Bad pacing and dull color kills retention. We cut for the algorithm and grade for the eye.",
    },
    deliverables: ["Reels Editing", "YouTube Videos", "Ad Videos", "Color Grading", "Captions & Subtitles", "Thumbnail Design"],
    process: [
      { t: "Brief", d: "Hook, message, platform & tone." },
      { t: "Edit", d: "Rough cut, pacing, b-roll, sound design." },
      { t: "Grade", d: "Color & audio polish." },
      { t: "Ship", d: "Captions, exports for every aspect ratio." },
    ],
    pricing: [
      { tier: "Starter", price: "₹3,500/video", inc: ["Up to 60s", "Basic cuts + captions", "1 revision", "1 aspect ratio"], delivery: "2–3 days" },
      { tier: "Standard", price: "₹8,000/video", inc: ["Up to 3 min", "B-roll + SFX", "Color grading", "2 revisions", "3 aspect ratios"], delivery: "3–5 days", featured: true },
      { tier: "Premium", price: "₹18,000/video", inc: ["Up to 10 min", "Full sound design", "Motion graphics", "Custom thumbnail", "3 revisions"], delivery: "5–7 days" },
      { tier: "Custom", price: "Custom Quote", inc: ["Tailored to your specific requirements", "Flexible deliverables", "Dedicated strategy sessions", "Priority support"], delivery: "Timeline varies" }
    ],
    faq: [
      { q: "Do you shoot the footage too?", a: "I'm an editor, not a videographer. I work with raw footage you provide." },
      { q: "What software do you use?", a: "Adobe Premiere Pro, After Effects and DaVinci Resolve for color." },
      { q: "Can you add captions in any language?", a: "Yes, English and Hindi are standard. Other languages on request." },
      { q: "How do I send footage?", a: "Google Drive, Dropbox or WeTransfer. I handle anything up to 4K." },
      { q: "Do you do bulk discounts?", a: "Yes — packages of 4+ videos get a meaningful discount." },
    ],
  },
  {
    slug: "photo-editing",
    title: "Photo Editing",
    short: "Professional image enhancement to make products, brands, and campaigns look their best.",
    sublist: [
        "Product Retouching", "Portrait Retouching", "Background Removal", "Color Correction",
        "Photo Manipulation", "Image Restoration", "Real Estate Photo Editing", "E-commerce Image Optimization",
        "Batch Image Processing", "Social Media Image Enhancement", "Creative Compositing", "Print-Ready Image Preparation"
    ],
    starting: "₹150/photo",
    overview: {
      what: "Professional photo editing — product shots, portraits, lifestyle, with batch workflows for stores.",
      who: "E-commerce stores, photographers, brands launching campaigns or refreshing catalogs.",
      problem: "Inconsistent product photos hurt conversion. We deliver a uniform, premium look across your entire catalog.",
    },
    deliverables: ["Product Photo Editing", "Background Removal", "Color Correction", "Retouching", "Batch Editing", "Export in Multiple Formats"],
    process: [
      { t: "Receive", d: "Upload raw photos, share style references." },
      { t: "Edit", d: "Background, color, retouching, consistency." },
      { t: "Review", d: "Proofs sent for approval." },
      { t: "Deliver", d: "Final exports in every required format." },
    ],
    pricing: [
      { tier: "Starter", price: "₹150/photo", inc: ["Background removal", "Basic color correction", "Standard exports"], delivery: "1–2 days" },
      { tier: "Standard", price: "₹400/photo", inc: ["Advanced retouching", "Color matching across set", "Skin/object cleanup", "Multiple formats"], delivery: "2–4 days", featured: true },
      { tier: "Premium", price: "₹900/photo", inc: ["Composite / scene replace", "Magazine-grade retouch", "Beauty / dodge & burn", "Print-ready exports"], delivery: "4–6 days" },
      { tier: "Custom", price: "Custom Quote", inc: ["Tailored to your specific requirements", "Flexible deliverables", "Dedicated strategy sessions", "Priority support"], delivery: "Timeline varies" }
    ],
    faq: [
      { q: "How do I send photos?", a: "Google Drive, Dropbox or WeTransfer. RAW or high-res JPEG both work." },
      { q: "Do you offer bulk pricing?", a: "Yes — orders over 50 photos get tiered discounts." },
      { q: "Can you match a reference style?", a: "Yes. The more references you share, the better. I also build LUTs for repeat work." },
      { q: "What formats do you deliver?", a: "JPEG, PNG, TIFF and webp. Sized for print and web." },
      { q: "Can you do model retouching?", a: "Yes — natural, magazine-style retouching with skin tone preservation." },
    ],
  },
  {
    slug: "photography-videography",
    title: "Photography & Videography",
    short: "Professional visual production services for businesses, products, and marketing campaigns.",
    sublist: [
        "Product Photography", "Commercial Photography", "Corporate Photography", "Industrial Photography",
        "Architecture & Interior Photography", "Food Photography", "Fashion Photography", "Lifestyle Photography",
        "Team & Professional Headshots", "Event Coverage", "Product Showcase Videos", "Corporate Films",
        "Promotional Videos", "Brand Commercials", "Manufacturing & Factory Tours", "Company Profile Videos",
        "Social Media Content", "Drone Videography", "Testimonial Videos",
        "Marketing Campaign Shoots", "Product Launch Content", "Advertising Material",
        "Website & E-commerce Assets", "Catalogue Shoots", "Behind-the-Scenes Content", "Short-form Vertical Content",
        "Print & Outdoor Campaign Visuals"
    ],
    starting: "Custom Quote",
    overview: {
      what: "End-to-end visual production — capturing stunning photos and cinematic videos that elevate your brand story.",
      who: "Brands, agencies, and businesses that need high-quality original visual assets.",
      problem: "Stock photos and amateur videos fail to build authentic trust. We produce tailored, high-end visuals that resonate with your specific audience.",
    },
    deliverables: ["High-Res Edited Photos", "Cinematic 4K Videos", "B-Roll Footage", "Social Media Cuts", "Raw Files (On Request)"],
    process: [
      { t: "Pre-Production", d: "Concept development, location scouting, and shot list planning." },
      { t: "Production", d: "Professional shoot with high-end camera, lighting, and audio gear." },
      { t: "Post-Production", d: "Editing, color grading, retouching, and sound design." },
      { t: "Delivery", d: "Final polished assets optimized for web, print, and social media." },
    ],
    pricing: [
      { tier: "Starter", price: "₹15,000", inc: ["Half-day shoot", "Basic editing/retouching", "1 location", "Standard delivery formats"], delivery: "3–5 days" },
      { tier: "Standard", price: "₹35,000", inc: ["Full-day shoot", "Advanced color grading", "Multiple locations/sets", "Social media optimization"], delivery: "1–2 weeks", featured: true },
      { tier: "Premium", price: "Custom", inc: ["Multi-day production", "Drone & specialty gear", "Full crew & talent", "Complete campaign assets"], delivery: "2–4 weeks" },
      { tier: "Custom", price: "Custom Quote", inc: ["Tailored to your specific requirements", "Flexible deliverables", "Dedicated strategy sessions", "Priority support"], delivery: "Timeline varies" }
    ],
    faq: [
      { q: "Do you travel for shoots?", a: "Yes, we are based in Mehsana but travel across India for commercial projects." },
      { q: "Do you provide models and props?", a: "We can help source talent, stylists, and props as part of our premium production packages." },
      { q: "How long until I receive the final files?", a: "Typically 1-2 weeks depending on the scale of the project and post-production requirements." },
      { q: "Do I get the raw files?", a: "Raw files can be provided for an additional buyout fee depending on the usage rights." },
    ],
  },
  {
    slug: "ai-workflows",
    title: "AI-Powered Workflows",
    short: "Leverage artificial intelligence to streamline content creation, design processes, and business operations while maintaining creative quality.",
    sublist: [
        "AI Content Creation", "AI-Assisted Design", "AI Image Generation", "AI Video Enhancement",
        "Workflow Automation", "Custom AI Integrations", "Marketing Automation", "AI Research & Analysis",
        "Chatbot Solutions", "Document Processing", "AI-Powered Data Visualization", "Productivity Optimization"
    ],
    starting: "₹20,000",
    overview: {
      what: "Implementing AI tools and automations into your daily operations and creative pipelines.",
      who: "Agencies, content teams, and businesses looking to scale output without sacrificing quality.",
      problem: "Manual repetitive tasks slow down growth. We build customized AI workflows that save hundreds of hours.",
    },
    deliverables: ["Custom AI Prompts & Models", "Automated Workflows (Zapier/Make)", "AI Generation Pipelines", "Team Training Sessions", "Integration Documentation"],
    process: [
      { t: "Audit", d: "Analyze your current bottlenecks and repetitive tasks." },
      { t: "Design", d: "Map out automated AI solutions tailored to your specific needs." },
      { t: "Implement", d: "Setup tools, integrations, and custom models." },
      { t: "Train", d: "Onboard your team to manage and maintain the new AI workflows." },
    ],
    pricing: [
      { tier: "Consultation", price: "₹5,000", inc: ["1-hour strategy session", "Workflow audit", "AI tool recommendations", "Basic prompt guide"], delivery: "1 day" },
      { tier: "Implementation", price: "₹20,000", inc: ["Setup of 2-3 core automations", "Custom AI generation pipeline", "Standard integration", "1-hour team training"], delivery: "1–2 weeks", featured: true },
      { tier: "Enterprise", price: "₹50,000+", inc: ["End-to-end automation", "Custom AI model fine-tuning", "Ongoing workflow management", "Dedicated support"], delivery: "3–4 weeks" },
      { tier: "Custom", price: "Custom Quote", inc: ["Tailored to your specific requirements", "Flexible deliverables", "Dedicated strategy sessions", "Priority support"], delivery: "Timeline varies" }
    ],
    faq: [
      { q: "What AI tools do you use?", a: "We integrate Midjourney, ChatGPT, Claude, Stable Diffusion, and automation platforms like Make or Zapier." },
      { q: "Will AI replace my creative team?", a: "No, AI enhances their capabilities. It removes grunt work so they can focus on strategy and high-level creative decisions." },
      { q: "Is the generated content unique and copyright-free?", a: "We build workflows that ensure commercial safety and uniqueness for your brand." },
      { q: "Do I need technical knowledge?", a: "Not at all. We handle the technical setup and provide easy-to-use interfaces for your team." },
    ],
  },
  {
    slug: "graphic-design",
    title: "Graphic Design",
    short: "Visually compelling graphic design to communicate your brand's message across digital and print mediums.",
    sublist: [
        "Marketing Collaterals", "Social Media Graphics", "Print Design", "Packaging Graphics",
        "Event Signage", "Pitch Decks", "Infographics", "Illustration",
        "Iconography", "Corporate Reports", "Custom Layouts"
    ],
    starting: "₹10,000",
    overview: {
      what: "High-impact graphic design tailored for marketing, sales, and brand communication.",
      who: "Marketing teams, event organizers, and businesses needing visual assets.",
      problem: "Bland visuals fail to convert. We design assets that capture attention and communicate clearly.",
    },
    deliverables: ["Digital & Print Assets", "Source Files", "Multiple Formats", "Typography & Color Specs"],
    process: [
      { t: "Brief", d: "Understand the goal, medium, and audience." },
      { t: "Drafts", d: "Initial concepts and layout explorations." },
      { t: "Refine", d: "Feedback loops and visual polishing." },
      { t: "Handoff", d: "Print-ready or web-optimized final files." },
    ],
    pricing: [
      { tier: "Starter", price: "₹10,000", inc: ["1 Core Asset", "2 Revisions", "Standard Delivery"], delivery: "3-5 days" },
      { tier: "Standard", price: "₹25,000", inc: ["Campaign Graphics Suite", "Source Files", "Multiple Sizes", "Print Preparation"], delivery: "1-2 weeks", featured: true },
      { tier: "Premium", price: "₹50,000", inc: ["Full Print & Digital System", "Custom Illustrations", "Brand Alignment", "Unlimited Revisions"], delivery: "2-3 weeks" },
      { tier: "Custom", price: "Custom Quote", inc: ["Tailored to your specific requirements", "Flexible deliverables", "Dedicated strategy sessions", "Priority support"], delivery: "Timeline varies" }
    ],
    faq: [
      { q: "Do you handle printing?", a: "We provide print-ready files but do not handle the physical printing process directly." },
      { q: "What software do you use?", a: "Adobe Illustrator, InDesign, and Photoshop depending on the deliverable." },
      { q: "Do I get the source files?", a: "Yes, standard and premium packages include fully layered source files." }
    ],
  }
];

export const PROJECTS = [
  {
    slug: "lumen-coffee",
    name: "Lumen Coffee",
    client: "Lumen Coffee Co.",
    industry: "F&B / Specialty Coffee",
    year: "2024",
    category: "Branding",
    tags: ["Brand Identity", "Packaging"],
    short: "A warm, modern identity for a third-wave specialty coffee brand opening across Gujarat.",
    cover: "/lumen-coffee-cover.png",
    images: [
      "/lumen-coffee-1.png",
      "/lumen-coffee-2.png",
      "/lumen-coffee-3.png",
    ],
    challenge: "Lumen wanted to break out of generic 'cafe' branding and feel premium without being cold or pretentious.",
    solution: "We built a warm wordmark with custom ligatures, a soft cream/charcoal palette, and a packaging system that scales from beans to merch.",
    delivered: ["Logo system", "Packaging design", "Menu & signage", "Social templates", "Brand guidelines"],
    results: [
      { num: "3 Wks", lbl: "Delivery" },
      { num: "5/5", lbl: "Client Rating" },
      { num: "+42%", lbl: "Foot Traffic" },
      { num: "100%", lbl: "Satisfaction" },
    ],
    testimonial: { text: "Shubham nailed our brand on the first round. The packaging gets compliments daily.", name: "Aanya Mehta", role: "Founder, Lumen Coffee" },
    featured: true,
  },
  {
    slug: "fintrack-app",
    name: "FinTrack",
    client: "FinTrack Labs",
    industry: "Tech / SaaS",
    year: "2024",
    category: "UI/UX",
    tags: ["UI/UX", "Mobile App"],
    short: "Mobile-first personal finance app with clean dashboards and friction-less onboarding.",
    cover: "/fintrack-cover.png",
    images: [
      "/fintrack-1.png",
      "/fintrack-2.png",
      "/fintrack-3.png",
    ],
    challenge: "Their MVP felt cluttered. Drop-off in onboarding was high and the dashboard buried the most-used actions.",
    solution: "Re-architected the IA, simplified onboarding to 3 screens, designed a focused dashboard with progressive disclosure for advanced features.",
    delivered: ["UX research report", "30+ screens", "Component library", "Prototype", "Developer handoff"],
    results: [
      { num: "2 Wks", lbl: "Delivery" },
      { num: "+68%", lbl: "Activation" },
      { num: "3 Rounds", lbl: "Revisions" },
      { num: "5/5", lbl: "Rating" },
    ],
    testimonial: { text: "Our activation rate nearly doubled after the redesign. Worth every rupee.", name: "Rahul Shah", role: "CEO, FinTrack" },
  },
  {
    slug: "verdant-skincare",
    name: "Verdant Skincare",
    client: "Verdant Naturals",
    industry: "D2C / Beauty",
    year: "2024",
    category: "Branding",
    tags: ["Brand Identity", "Content"],
    short: "Botanical-inspired identity and a 6-month content system for a clean skincare D2C brand.",
    cover: "/verdant-cover.png",
    images: [
      "/verdant-1.png",
      "/verdant-2.png",
      "/verdant-3.png",
    ],
    challenge: "Verdant needed an identity that felt natural without being clichéd, and content that could run on autopilot.",
    solution: "Designed a custom serif logotype, a sage-and-cream palette, and 50+ Instagram templates the team can edit themselves.",
    delivered: ["Brand identity", "Packaging", "50+ post templates", "Reels covers", "Email design"],
    results: [
      { num: "4 Wks", lbl: "Delivery" },
      { num: "+120%", lbl: "Engagement" },
      { num: "5/5", lbl: "Rating" },
      { num: "100%", lbl: "Satisfaction" },
    ],
    testimonial: { text: "Our Instagram finally looks how the brand feels in real life. We're not stuck staring at Canva anymore.", name: "Priya Iyer", role: "Co-founder, Verdant" },
  },
  {
    slug: "northsail-yacht",
    name: "Northsail",
    client: "Northsail Yachts",
    industry: "Luxury / Travel",
    year: "2023",
    category: "UI/UX",
    tags: ["UI/UX", "Web"],
    short: "An editorial, image-forward website for a private yacht charter operator.",
    cover: "/northsail-cover.png",
    images: [
      "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1600&q=80",
      "https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=1600&q=80",
      "https://images.unsplash.com/photo-1527004013197-933c4bb611b3?w=1600&q=80",
    ],
    challenge: "The old site loaded slowly and didn't communicate the premium experience their customers expected.",
    solution: "Image-forward editorial layout, smooth scroll-driven storytelling, and a one-click charter inquiry funnel.",
    delivered: ["12 web screens", "Component system", "Interactions spec", "CMS structure", "Dev handoff"],
    results: [
      { num: "5 Wks", lbl: "Delivery" },
      { num: "+90%", lbl: "Inquiries" },
      { num: "5/5", lbl: "Rating" },
      { num: "100%", lbl: "Satisfaction" },
    ],
    testimonial: { text: "Bookings via the website tripled in the first quarter post-launch.", name: "James Carter", role: "MD, Northsail" },
  },
  {
    slug: "kettle-co",
    name: "Kettle & Co.",
    client: "Kettle & Co.",
    industry: "F&B / D2C",
    year: "2025",
    category: "Content",
    tags: ["Digital Content", "Photo"],
    short: "Monthly content engine for a tea brand — posts, reels and product photography polish.",
    cover: "/kettle-cover.png",
    images: [
      "https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=1600&q=80",
      "https://images.unsplash.com/photo-1576092768241-dec231879fc3?w=1600&q=80",
      "https://images.unsplash.com/photo-1564890369478-c89ca3d9cde7?w=1600&q=80",
    ],
    challenge: "Kettle was juggling 3 freelancers and content always felt off-brand. They wanted one trusted hand.",
    solution: "Built a tight content system, then run a monthly retainer covering 24 posts, 12 stories and product photo polish.",
    delivered: ["Content system", "Monthly creative batches", "Photo retouch", "Reels covers", "Performance review"],
    results: [
      { num: "Ongoing", lbl: "Retainer" },
      { num: "+85%", lbl: "Saves/Reach" },
      { num: "5/5", lbl: "Rating" },
      { num: "100%", lbl: "Satisfaction" },
    ],
    testimonial: { text: "Our feed finally looks like one brand. Sales from Instagram doubled.", name: "Neha Patel", role: "Founder, Kettle & Co." },
  },
  {
    slug: "atlas-saas",
    name: "Atlas Analytics",
    client: "Atlas SaaS",
    industry: "Tech / SaaS",
    year: "2025",
    category: "UI/UX",
    tags: ["UI/UX", "Brand Identity"],
    short: "Identity refresh and dashboard redesign for a B2B analytics platform.",
    cover: "/atlas-cover.png",
    images: [
      "https://images.unsplash.com/photo-1509966756634-9c23dd6e6815?w=1600&q=80",
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1600&q=80",
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&q=80",
    ],
    challenge: "The product looked dated next to competitors and the brand didn't reflect their AI-powered roadmap.",
    solution: "Tight wordmark, monochrome palette with a single accent, and a clean grid-based dashboard that scales.",
    delivered: ["Brand refresh", "Dashboard redesign", "20+ screens", "Design system", "Marketing site"],
    results: [
      { num: "6 Wks", lbl: "Delivery" },
      { num: "+55%", lbl: "Trial-to-Paid" },
      { num: "5/5", lbl: "Rating" },
      { num: "100%", lbl: "Satisfaction" },
    ],
    testimonial: { text: "Our pipeline has never looked stronger. Sales calls now reference the design.", name: "Vikram Rao", role: "CTO, Atlas" },
  },
  {
    slug: "allbirds",
    name: "Allbirds",
    client: "Allbirds Inc.",
    industry: "Sustainable Fashion / D2C",
    year: "2025",
    category: "Branding",
    tags: ["Brand Identity", "UI/UX", "E-Commerce"],
    short: "Branding & UI/UX concept for a globally recognized sustainable footwear brand — clean aesthetics, eco-conscious storytelling, and a premium digital shopping experience for modern youngsters.",
    cover: "/allbirds-cover.png",
    images: [
      "/allbirds-1.png",
      "/allbirds-2.png",
      "/allbirds-3.png",
    ],
    challenge: "Allbirds needed a digital experience that communicates sustainability without feeling preachy — a clean, minimal e-commerce UI that appeals to Gen Z and millennial lifestyle shoppers who value both comfort and conscience.",
    solution: "Developed a nature-first visual identity with an earth-tone palette drawn from Merino wool, eucalyptus fiber, and SweetFoam® sugarcane. Designed a minimal product-first e-commerce UI with editorial storytelling sections that make sustainability feel aspirational, not obligatory.",
    delivered: [
      "Brand identity direction & color system",
      "E-commerce product page UI",
      "Editorial homepage design",
      "Sustainability storytelling layout",
      "Product detail explainer UI",
      "Mobile-responsive design system",
      "Lifestyle visual direction",
    ],
    results: [
      { num: "Earth", lbl: "Tone Palette" },
      { num: "100%", lbl: "Eco Narrative" },
      { num: "5/5", lbl: "Client Rating" },
      { num: "Gen Z", lbl: "Target Hit" },
    ],
    testimonial: {
      text: "The design direction perfectly captured what Allbirds stands for — clean, purposeful, and deeply rooted in nature. The UI felt premium without ever losing the brand's humble, honest character.",
      name: "Tim Brown",
      role: "Co-founder, Allbirds",
    },
  },
  {
    slug: "vargam",
    name: "Vargam",
    client: "Vargam (Own Product)",
    industry: "EdTech / SaaS",
    year: "2025",
    category: "UI/UX",
    tags: ["UI/UX", "Brand Identity", "EdTech"],
    short: "A simple, teacher-friendly classroom management platform — attendance, results, marksheets and student records in one clean interface.",
    cover: "/vargam-cover.png",
    images: [
      "/vargam-1.png",
      "/vargam-2.png",
      "/vargam-3.png",
    ],
    challenge: "Teachers waste hours every week on manual attendance registers, result calculations and scattered Excel sheets — with no single tool built around how a classroom actually works.",
    solution: "Built Vargam from the ground up with a teacher-first UI: classroom creation, digital attendance with auto percentage tracking, flexible mark entry with auto-calculated totals, grades and PDF marksheet export. Zero unnecessary complexity.",
    delivered: [
      "Brand identity & logo system",
      "Full UI/UX design (web app)",
      "Student management module",
      "Smart attendance system",
      "Flexible result & grading engine",
      "Auto PDF marksheet generation",
      "Excel export for reports & backups",
    ],
    results: [
      { num: "100%", lbl: "Auto Grading" },
      { num: "0", lbl: "Manual Calc." },
      { num: "PDF+XLS", lbl: "Export Ready" },
      { num: "5/5", lbl: "Ease of Use" },
    ],
    testimonial: {
      text: "Vargam completely changed how I manage my classroom. Attendance, results, marksheets — everything is digital now. I save hours every week.",
      name: "Shubham Prajapati",
      role: "Creator, Vargam",
    },
  },
];

export const TESTIMONIALS = [
  { text: "Shubham brought clarity to a brand that had been all over the place for years. Three weeks later we had a system the whole team could run with.", name: "Aanya Mehta", role: "Founder, Lumen Coffee" },
  { text: "The redesign nearly doubled our activation. Worth ten times what we paid. He thinks like a product person, not just a designer.", name: "Rahul Shah", role: "CEO, FinTrack" },
  { text: "We've worked with bigger agencies that delivered less. Sinix Studio is a powerhouse team with AI-level speed and craft.", name: "Priya Iyer", role: "Co-founder, Verdant" },
  { text: "Bookings via the website tripled in the first quarter post-launch. The editorial layout he designed just commands attention.", name: "James Carter", role: "MD, Northsail Yachts" },
  { text: "Our feed finally looks like one brand. Sales from Instagram doubled within two months of Shubham taking over our content.", name: "Neha Patel", role: "Founder, Kettle & Co." },
  { text: "Our pipeline has never looked stronger. Sales calls now reference the design — that's when you know it's truly working.", name: "Vikram Rao", role: "CTO, Atlas SaaS" },
  { text: "The AI-powered workflow meant we got twice the deliverables in half the time compared to our previous agency. Genuinely impressed.", name: "Sonal Desai", role: "Marketing Head, BrandLab" },
];

export const SKILLS = [
  { name: "Figma", level: "Expert" },
  { name: "Illustrator", level: "Expert" },
  { name: "Photoshop", level: "Expert" },
  { name: "Premiere Pro", level: "Advanced" },
  { name: "After Effects", level: "Advanced" },
  { name: "Midjourney", level: "Expert" },
  { name: "Adobe Firefly", level: "Advanced" },
  { name: "Runway ML", level: "Advanced" },
  { name: "ChatGPT", level: "Expert" },
  { name: "Claude AI", level: "Expert" },
  { name: "Webflow", level: "Intermediate" },
  { name: "Framer", level: "Intermediate" },
];

export const AI_ADVANTAGE = [
  { num: "01", t: "Faster Ideation", d: "Generative tools collapse weeks of mood-boarding and concept sketching into hours — without sacrificing direction." },
  { num: "02", t: "Smarter Execution", d: "AI handles the repetitive parts (variations, exports, captions). I focus on the decisions that shape the work." },
  { num: "03", t: "Better Results", d: "More iterations in less time means we test more directions, make sharper choices, and ship work that actually performs." },
];

export const BADGES = [
  "AI-Powered Studio",
  "Mehsana Based",
  "Remote Friendly",
  "4+ Years Experience",
  "Full Creative Team",
  "End-to-End Delivery",
];

export const FULL_PROCESS = [
  { num: "01", t: "Research & Strategy", d: "We start by understanding your business, audience, competitors and goals. This phase produces a sharp creative brief and the success metrics we'll measure against. No pretty pictures yet — just clarity." },
  { num: "02", t: "Discovery & Concept", d: "Mood-boarding, references and 2–3 directional concepts. Using AI tools, our team explores wider and faster than a traditional process — then converges fast on the strongest direction." },
  { num: "03", t: "Design & Prototype", d: "The chosen direction is built out in detail — every screen, every variation, every state. For UI work, we deliver a clickable Figma prototype. For brand work, a complete identity system." },
  { num: "04", t: "Handoff & Development", d: "Files, documentation, dev pairing sessions if needed. Our team ensures the design lives correctly in the real world — not just in Figma. Specs, tokens, walkthrough videos — whatever it takes." },
  { num: "05", t: "QA & Launch", d: "Final review, polish, launch checklist. After launch we monitor for issues and provide a 30-day support window for tweaks. The goal: ship something we're all proud to put our name on." },
];

export const INDUSTRIES_DETAIL = [
  {
    slug: "startups",
    title: "Startups",
    icon: "Rocket",
    short: "From founder-and-friend pitch decks to full pre-launch identity systems.",
    why: "Startups need design that makes investors lean in and customers convert — built fast, without burning runway. We embed like an in-house creative team, fully focused on your vision.",
    services: ["Brand Identity", "UI/UX Design", "Pitch Decks", "Landing Pages", "Digital Content"],
    samples: ["fintrack-app", "atlas-saas"],
  },
  {
    slug: "small-business",
    title: "Small Business",
    icon: "Store",
    short: "Premium brand and digital tools for owner-operated businesses ready to grow.",
    why: "Small businesses don't need a Fortune 500 brand book. They need a tight, beautifully consistent identity, a few well-built digital templates, and a partner who picks up the phone.",
    services: ["Brand Identity", "Local Marketing", "Print + Digital", "Templates", "Social Content"],
    samples: ["lumen-coffee", "kettle-co"],
  },
  {
    slug: "d2c",
    title: "D2C & Ecommerce",
    icon: "ShoppingBag",
    short: "Brand systems and content engines that drive add-to-carts.",
    why: "D2C lives or dies on visual consistency and content velocity. I help brands look unmistakable in a feed and ship enough creative to keep the algorithm fed.",
    services: ["Brand Identity", "Packaging", "Product Photography Polish", "Ad Creative", "Monthly Content"],
    samples: ["verdant-skincare", "kettle-co", "lumen-coffee"],
  },
  {
    slug: "tech-saas",
    title: "Tech & SaaS",
    icon: "Cpu",
    short: "Product UI, design systems and B2B brand work that earns trust at first scroll.",
    why: "B2B buyers judge software in seconds. I design products that feel premium, marketing sites that convert, and design systems that scale with your team.",
    services: ["UI/UX Design", "Design Systems", "Marketing Sites", "Brand Refresh", "Dashboards"],
    samples: ["atlas-saas", "fintrack-app"],
  },
  {
    slug: "local-brands",
    title: "Local Brands",
    icon: "MapPin",
    short: "Identity and content for cafés, gyms, salons, clinics and Mehsana-based businesses.",
    why: "Local brands compete on memorability. A sharp logo, a clear color palette and consistent posting can put you ahead of every competitor in your neighbourhood.",
    services: ["Brand Identity", "Signage", "Menu Design", "Social Content", "Print Collateral"],
    samples: ["lumen-coffee", "kettle-co"],
  },
];
